import { Button } from '@ophelos/ui/components/button';
import { Loader } from '@ophelos/ui/components/loader';
import { useAuth } from '@ophelos/ui/contexts/workos-auth-context';
import { ShieldX } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { MarketSwitcher } from '@/components/auth/MarketSwitcher';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { SignOutOverlay, useSignOut } from '@/hooks/useSignOut';
import { getMarketHubs } from '@/lib/market-hubs';
import { getMarketOrganizationId, isSessionAlignedWithMarket } from '@/lib/market-organization';

interface MarketOrganizationGuardProps {
  children: ReactNode;
}

/**
 * Ensures the WorkOS session org matches this market's baked-in org before Hub
 * loads operator context. Fixes cross-market navigation (hub.intrum.pt ↔ .fr)
 * where AuthKit restores a session for the wrong organisation.
 */
export function MarketOrganizationGuard({ children }: MarketOrganizationGuardProps) {
  const { organizationId, isLoading, switchToOrganization } = useAuth();
  const marketOrganizationId = getMarketOrganizationId();
  const { isSigningOut, handleSignOut } = useSignOut();
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const attemptedMarketOrgRef = useRef<string | null>(null);

  const isAligned = isSessionAlignedWithMarket(organizationId);
  const markets = getMarketHubs();

  useEffect(() => {
    if (isLoading || !marketOrganizationId || !organizationId || isAligned) {
      return;
    }

    if (attemptedMarketOrgRef.current === marketOrganizationId) {
      return;
    }
    attemptedMarketOrgRef.current = marketOrganizationId;

    let cancelled = false;

    const targetOrganizationId = marketOrganizationId;

    async function alignSessionWithMarket() {
      setSwitchError(null);
      setIsSwitching(true);
      try {
        await switchToOrganization({ organizationId: targetOrganizationId });
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to switch to market organization:', error);
        setSwitchError('Your account is not assigned to this market. Use the Hub for your assigned market or contact your administrator.');
      } finally {
        if (!cancelled) {
          setIsSwitching(false);
        }
      }
    }

    void alignSessionWithMarket();

    return () => {
      cancelled = true;
    };
  }, [isLoading, marketOrganizationId, organizationId, isAligned, switchToOrganization]);

  if (!marketOrganizationId) {
    return <>{children}</>;
  }

  if (isLoading || isSwitching || (!!organizationId && !isAligned && !switchError)) {
    return <Loader text="Aligning with this market..." fullScreen />;
  }

  if (switchError) {
    return (
      <div className="flex h-screen items-center justify-center p-4">
        <SignOutOverlay visible={isSigningOut} />
        <div className="w-full max-w-md">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="error">
                <ShieldX />
              </EmptyMedia>
              <EmptyTitle>Wrong market</EmptyTitle>
              <EmptyDescription>{switchError}</EmptyDescription>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                {markets && <MarketSwitcher markets={markets} />}
                <Button variant="default" onClick={handleSignOut}>
                  Sign out
                </Button>
              </div>
            </EmptyHeader>
          </Empty>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
